import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { CreditCard, Check, Sparkles, BedDouble, ShieldCheck, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  trialing: { label: 'Free trial', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  beta: { label: 'Beta (complimentary)', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  founder: { label: 'Founder (complimentary)', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  past_due: { label: 'Payment past due', color: 'bg-red-100 text-red-800 border-red-200' },
  canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const Billing = () => {
  const [status, setStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState('monthly');
  const [beds, setBeds] = useState('');
  const [contact, setContact] = useState({ contact_name: '', email: '', phone: '', message: '' });

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/billing/status`),
        axios.get(`${API}/pricing`),
      ]);
      setStatus(s.data);
      setPricing(p.data);
      setCycle(s.data.billing_cycle || 'monthly');
      setBeds(s.data.bed_count ? String(s.data.bed_count) : '');
    } catch (e) {
      toast.error('Could not load billing information');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reconcile after returning from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) {
      (async () => {
        try {
          await axios.post(`${API}/billing/sync`);
          toast.success('Payment method saved — your subscription is set up!');
        } catch (e) { /* best effort */ }
        window.history.replaceState({}, '', '/billing');
        load();
      })();
    } else if (params.get('checkout') === 'cancelled') {
      toast.info('Checkout cancelled — no changes made.');
      window.history.replaceState({}, '', '/billing');
    }
  }, [load]);

  const maxBeds = pricing?.max_self_serve_beds || 300;
  const bedNum = parseInt(beds) || 0;
  const overCap = bedNum > maxBeds;

  const tierForBeds = (b) => {
    if (!pricing) return null;
    const order = ['tier_50', 'tier_100', 'tier_150', 'tier_200', 'tier_250', 'tier_300'];
    for (const k of order) if (b <= pricing.tiers[k].max_beds) return { key: k, ...pricing.tiers[k] };
    return null;
  };
  const selectedTier = overCap ? null : tierForBeds(bedNum || status?.bed_count || 0);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        billing_cycle: cycle,
        bed_count: bedNum || status?.bed_count,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : (detail || 'Could not start checkout'));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/billing/portal`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open billing portal');
      setBusy(false);
    }
  };

  const submitContact = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/billing/contact-sales`, {
        shelter_name: status?.shelter_name || 'My Shelter',
        contact_name: contact.contact_name,
        email: contact.email,
        phone: contact.phone,
        bed_count: bedNum,
        message: contact.message,
      });
      toast.success('Thanks! Our team will reach out about a custom plan.');
      setContact({ contact_name: '', email: '', phone: '', message: '' });
    } catch (e) {
      toast.error('Could not send your request. Please try again.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  }

  const meta = STATUS_LABELS[status?.subscription_status] || STATUS_LABELS.trialing;
  const grandfathered = status?.is_grandfathered;
  const bedsPct = status?.bed_limit ? Math.min(100, Math.round((status.beds_used / status.bed_limit) * 100)) : 0;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><CreditCard className="h-7 w-7 text-indigo-600" /> Billing & Plan</h1>
        <p className="text-gray-600 mt-1">Manage your subscription, payment method, and bed capacity.</p>
      </div>

      {/* Current plan summary */}
      <Card className="border-0 card-shadow-lg" data-testid="current-plan-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              {status?.tier_name || 'Your Plan'}
              <Badge className={`${meta.color} border`} data-testid="subscription-status-badge">{meta.label}</Badge>
            </CardTitle>
            {!grandfathered && status?.has_payment_method && (
              <Button variant="outline" size="sm" onClick={openPortal} disabled={busy} data-testid="manage-billing-btn">
                <ExternalLink className="h-4 w-4 mr-1" /> Manage billing
              </Button>
            )}
          </div>
          <CardDescription>
            {grandfathered
              ? 'You have complimentary access as a beta / founding shelter — no payment required. Thank you for being an early partner!'
              : status?.subscription_status === 'trialing'
                ? `You're on a free trial${status?.trial_days_left != null ? ` — ${Math.max(0, status.trial_days_left)} day(s) left` : ''}. Add a payment method to continue uninterrupted.`
                : status?.subscription_status === 'active'
                  ? 'Your subscription is active. Thank you!'
                  : status?.subscription_status === 'past_due'
                    ? 'Your last payment failed. Please update your payment method to keep access.'
                    : 'Choose a plan below to activate billing.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {grandfathered && (
            <div className="flex items-center gap-2 text-purple-700 bg-purple-50 border border-purple-100 rounded-lg px-4 py-3" data-testid="grandfathered-banner">
              <Sparkles className="h-5 w-5" /> Complimentary plan — billing is waived for your shelter.
            </div>
          )}
          {/* Bed usage */}
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-1.5 text-gray-700"><BedDouble className="h-4 w-4" /> Beds in use</span>
              <span className="font-medium" data-testid="bed-usage-text">{status?.beds_used} / {grandfathered ? '∞' : status?.bed_limit}</span>
            </div>
            {!grandfathered && (
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full ${bedsPct >= 100 ? 'bg-red-500' : bedsPct >= 80 ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${bedsPct}%` }} />
              </div>
            )}
            {!grandfathered && bedsPct >= 100 && (
              <p className="text-xs text-red-600 mt-1">You've reached your bed limit. Increase your bed count below to add more.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Plan selection / checkout (hidden for grandfathered) */}
      {!grandfathered && (
        <Card className="border-0 card-shadow-lg" data-testid="choose-plan-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-indigo-600" /> {status?.has_payment_method ? 'Change your plan' : 'Set up your subscription'}</CardTitle>
            <CardDescription>$50/month per 50 beds. Annual billing gives you 2 months free.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600">Number of beds</label>
                <Input type="number" min="1" value={beds} onChange={(e) => setBeds(e.target.value)} placeholder="e.g. 40" data-testid="billing-bed-count" />
              </div>
              <div>
                <label className="text-sm text-gray-600">Billing cycle</label>
                <div className="flex gap-2 mt-1">
                  {['monthly', 'annual'].map((c) => (
                    <button key={c} type="button" onClick={() => setCycle(c)}
                      className={`flex-1 rounded-md border px-3 py-2 text-sm capitalize ${cycle === c ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-medium' : 'border-gray-200 text-gray-600'}`}
                      data-testid={`billing-cycle-${c}`}>
                      {c}{c === 'annual' ? ' (2 mo free)' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {overCap ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="enterprise-contact-form">
                <p className="text-sm text-amber-800 font-medium">Shelters over {maxBeds} beds get a custom plan. Tell us about your needs:</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input placeholder="Your name" value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} data-testid="contact-name" />
                  <Input type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
                  <Input placeholder="Phone (optional)" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" />
                </div>
                <Input placeholder="Anything else we should know?" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" />
                <Button onClick={submitContact} disabled={busy || !contact.contact_name || !contact.email} className="bg-amber-600 hover:bg-amber-700" data-testid="contact-sales-submit">
                  {busy ? 'Sending...' : 'Contact sales'}
                </Button>
              </div>
            ) : (
              <>
                {selectedTier && (
                  <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-4 flex items-center justify-between" data-testid="selected-plan-summary">
                    <div>
                      <p className="font-semibold text-indigo-900">{selectedTier.name}</p>
                      <p className="text-sm text-indigo-700">Billed {cycle}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-900">${cycle === 'monthly' ? selectedTier.monthly : selectedTier.annual}</p>
                      <p className="text-xs text-indigo-600">/{cycle === 'monthly' ? 'month' : 'year'}</p>
                    </div>
                  </div>
                )}
                {status?.subscription_status === 'trialing' && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <AlertDescription className="text-blue-800 text-sm flex items-center gap-1.5">
                      <Check className="h-4 w-4" /> Your card won't be charged until your {pricing?.trial_days || 30}-day trial ends. Cancel anytime.
                    </AlertDescription>
                  </Alert>
                )}
                <Button onClick={startCheckout} disabled={busy || !selectedTier} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="start-checkout-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                  {status?.has_payment_method ? 'Update subscription' : 'Add payment method & subscribe'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Billing;
